import React, { useState } from "react";
import { Card, CardHeader, CardBody, Collapse, Badge } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import JsonNode from "./JsonNode";

interface MainSelectorsDisplayProps {
  parsedData: any;
  mainSelectors: string[];
}

// Fonction utilitaire pour copier du texte dans le presse-papiers
const copyToClipboard = (text: string) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      // Succès silencieux
    })
    .catch((err) => {
      console.error("Erreur lors de la copie du chemin:", err);
    });
};

const MainSelectorsDisplay: React.FC<MainSelectorsDisplayProps> = ({
  parsedData,
  mainSelectors,
}) => {
  const [expandedSelectors, setExpandedSelectors] = useState<{
    [key: string]: boolean;
  }>({});
  const [notificationVisible, setNotificationVisible] = useState(false);
  const [copiedPath, setCopiedPath] = useState("");

  // Fonction pour basculer l'état d'un main selector
  const toggleSelector = (selectorName: string) => {
    setExpandedSelectors((prev) => ({
      ...prev,
      [selectorName]: !prev[selectorName],
    }));
  };

  // Fonction pour gérer le clic sur un chemin
  const handlePathClick = (selectorName: string, path: string) => {
    const fullPath = `@${selectorName}.${path}`;
    copyToClipboard(fullPath);
    setCopiedPath(fullPath);

    // Afficher la notification
    setNotificationVisible(true);

    // Masquer la notification après un délai
    setTimeout(() => {
      setNotificationVisible(false);
    }, 2000);
  };

  // Fonction pour gérer le clic sur la racine du selector
  const handleRootClick = (selectorName: string) => {
    const fullPath = `@${selectorName}`;
    copyToClipboard(fullPath);
    setCopiedPath(fullPath);

    // Afficher la notification
    setNotificationVisible(true);

    // Masquer la notification après un délai
    setTimeout(() => {
      setNotificationVisible(false);
    }, 2000);
  };

  // Filtrer les main selectors qui ont des données
  const availableMainSelectors = mainSelectors.filter(
    (selectorName) => parsedData && parsedData[selectorName]
  );

  if (availableMainSelectors.length === 0) {
    return null;
  }

  return (
    <div className="main-selectors-container">
      <h4>Main Selectors</h4>
      
      {availableMainSelectors.map((selectorName) => {
        const selectorData = parsedData[selectorName];
        const isExpanded = expandedSelectors[selectorName];

        return (
          <Card key={selectorName} className="main-selector-card">
            <CardHeader
              className="main-selector-header d-flex justify-content-between align-items-center"
              onClick={() => toggleSelector(selectorName)}
            >
              <div className="d-flex align-items-center">
                <FontAwesomeIcon
                  icon={isExpanded ? faChevronDown : faChevronRight}
                  className="mr-2"
                />
                <Badge
                  className="main-selector-badge mr-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRootClick(selectorName);
                  }}
                  title={`Cliquer pour copier @${selectorName}`}
                >
                  {selectorName}
                </Badge>
                <FontAwesomeIcon
                  icon={faCopy}
                  className="text-muted"
                  size="sm"
                  title="Cliquer sur les éléments pour copier le chemin"
                />
              </div>
              <span className="text-muted small">
                {Array.isArray(selectorData)
                  ? `${selectorData.length} éléments`
                  : typeof selectorData === "object" && selectorData !== null
                  ? `${Object.keys(selectorData).length} propriétés`
                  : "Valeur simple"}
              </span>
            </CardHeader>
            <Collapse isOpen={isExpanded}>
              <CardBody style={{ padding: "0.5rem" }}>
                <div className="main-selector-content">
                  <JsonNode
                    data={selectorData}
                    onPathClick={(path) => handlePathClick(selectorName, path)}
                    isMainSelector={true}
                    mainSelectorName={selectorName}
                  />
                </div>
              </CardBody>
            </Collapse>
          </Card>
        );
      })}

      {notificationVisible && (
        <div className="main-selector-copy-notification">
          <div className="notification-title">Chemin copié !</div>
          <div className="notification-path">{copiedPath}</div>
        </div>
      )}
    </div>
  );
};

export default MainSelectorsDisplay; 